import { CheckCircle, Circle, Lock } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

const PIPELINE_STEPS = {
  en: [
    { key: "submission", label: "Submission" },
    { key: "pre_evaluation", label: "Pre-Evaluation" },
    { key: "evaluation", label: "Evaluation" },
    { key: "deal_monitor", label: "Listing" },
    { key: "pre_financing", label: "Pre-Financing" },
    { key: "nda_signed", label: "NDA Signed" },
    { key: "buyer_contact", label: "Contact" },
    { key: "loi_pending", label: "LOI Pending" },
    { key: "loi_signed", label: "LOI Signed" },
    { key: "closing", label: "Closing" },
  ],
  fr: [
    { key: "submission", label: "Soumission" },
    { key: "pre_evaluation", label: "Pré-évaluation" },
    { key: "evaluation", label: "Évaluation" },
    { key: "deal_monitor", label: "Annonce" },
    { key: "pre_financing", label: "Pré-financement" },
    { key: "nda_signed", label: "NDA signé" },
    { key: "buyer_contact", label: "Contact" },
    { key: "loi_pending", label: "LOI en attente" },
    { key: "loi_signed", label: "LOI signée" },
    { key: "closing", label: "Conclusion" },
  ],
};

interface DealPipelineStepperProps {
  currentStage: string;
  dealName: string;
}

export function DealPipelineStepper({ currentStage, dealName }: DealPipelineStepperProps) {
  const { language } = useLanguage();
  const steps = PIPELINE_STEPS[language as keyof typeof PIPELINE_STEPS] || PIPELINE_STEPS.en;
  const currentIndex = steps.findIndex(s => s.key === currentStage);

  return (
    <div className="bg-card rounded-lg border p-4">
      <h4 className="font-semibold text-sm mb-3 text-foreground">{dealName}</h4>
      <div className="flex items-center gap-0.5 overflow-x-auto pb-2">
        {steps.map((step, index) => {
          const isComplete = index < currentIndex;
          const isCurrent = index === currentIndex;
          const isLocked = index > currentIndex;

          return (
            <div key={step.key} className="flex items-center flex-shrink-0">
              <div className="flex flex-col items-center">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
                  isComplete ? 'bg-green-500 text-white' :
                  isCurrent ? 'bg-primary text-primary-foreground ring-2 ring-primary/30' :
                  'bg-muted text-muted-foreground'
                }`}>
                  {isComplete ? <CheckCircle className="h-4 w-4" /> :
                   isLocked ? <Lock className="h-3 w-3" /> :
                   <Circle className="h-3 w-3" />}
                </div>
                <span className={`text-[10px] mt-1 text-center w-16 leading-tight ${
                  isCurrent ? 'font-semibold text-primary' :
                  isComplete ? 'text-green-600' :
                  'text-muted-foreground'
                }`}>
                  {step.label}
                </span>
              </div>
              {index < steps.length - 1 && (
                <div className={`w-4 h-0.5 mt-[-12px] ${
                  isComplete ? 'bg-green-500' : 'bg-muted'
                }`} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}